import React from "react";
import { memo, useEffect, useRef, useMemo, useState, ReactNode,} from "react";
import { isObject,  debounce } from 'lodash-es';
import { useInViewport, useUnmount, useDebounceFn, useSafeState } from 'ahooks'
import { Options as UseInViewportOptions } from 'ahooks/es/useInViewport'
import { DebounceOptions } from 'ahooks/es/useDebounce/debounceOptions'

export interface UseLazyRenderOptions extends UseInViewportOptions {}
export interface UseLazyRenderOptions extends DebounceOptions {}
export interface UseLazyRenderOptions {
  forceVisible?: boolean
  placeholder?: ReactNode
  placeholderWrapperClassName?: string
  content?: ReactNode
  debugLog?: boolean,
  children:ReactNode,
  Persistent?:boolean,
}


interface UseMonitorVisibleProps<D = (dom: HTMLElement) => void> {
    children: any;
    // 是否一直有效
    readonly always: boolean;
    // 曝光时的回调，若不存在always，则只执行一次
    onExpose: D;
    // 曝光后又隐藏的回调，若不存在always，则只执行一次
    onHide: D;
    // IntersectionObserver相关的配置
    observerOptions: IntersectionObserverInit;
    // 页面不可见时，是否hidden元素，这将会将传入的children节点hidden，
    // 各种副作用将不生效，比如请求，dispatch，定时等等，减少dom节点，提高组件性能,反之，则正常，默认为true，做销毁处理,如果要做特殊处理,开启即可
    isDestroyElement: boolean;
}

let times = 0
const logRenderTimes = debounce(() => {
  // console.log('lazy-render times:', times)
  times = 0
}, 128)

/**
 * 监听元素的曝光
 * @param {UseMonitorVisibleProps} props 要监听的元素和回调
 * @returns {JSX.Element}
 */
// React.forwardRef<HTMLDivElement, Partial<UseMonitorVisibleProps>>
// 待废弃
export const UseMonitorVisible = memo(
    (props: Partial<UseMonitorVisibleProps>): JSX.Element => {
        // useWhyDidYouUpdate('props isRender',props)
        // 容器
        const ref = useRef<any>(null);
        // 和UI视图渲染没有关系，用ref存储，作用于当前组件的可见标识
        const curExpose = useRef(false);
        // 是否不可见状态下清空children节点
        const curIsDestroy = useRef(props.isDestroyElement || true);
        // 控制children可见
        const [stat, setStat] = useState<boolean>(false);
        useEffect(() => {
            if (ref.current) {
                // Dom节点
                const target = ref.current;
                // observer配置项
                const observerOptions: any = props?.observerOptions || {
                    threshold: [0, 0.5, 1]
                };
                // observer回调
                const intersectionCallback = (entries: IntersectionObserverEntry[]) => {
                    const [entry] = entries;
                    // 可见
                    if (entry.isIntersecting) {
                        if (entry.intersectionRatio >= observerOptions.threshold[1]) {
                            // 页面可见时把节点导出
                            if (!curExpose.current) {
                                props?.onExpose?.(target);
                            }
                            curIsDestroy.current && setStat(true);
                            curExpose.current = true;
                            if (!props?.always && typeof props?.onHide !== 'function') {
                                // 当组件没有传递always属性时且没有onHide方式时
                                // 则在执行一次曝光后，移除监听
                                io.unobserve(target);
                            }
                        }
                        // 不可见
                    } else if (typeof props?.onHide === 'function' && curExpose.current) {
                        // 页面不可见时把节点导出
                        props.onHide(target);
                        curIsDestroy.current && setStat(true);
                        curExpose.current = false;
                        // 和可见方法同理
                        if (!props?.always) {
                            io.unobserve(target);
                        }
                    }
                };
                // new observer
                const io = new IntersectionObserver(intersectionCallback, observerOptions);
                // register observer
                io.observe(target);
                // destroy observer
                return () => io.unobserve(target);
            }
        }, [props, ref]);

        // 当传递的children不是虚拟Dom节点，则直接返回,如类似数组，对象，变量等传入，以上类型在fiber中不存在,执行cloneElement将报错，提前返回空节点
        if (!props?.children.type) {
            // console.log('Error: 必须传递一个Dom节点类型,包含虚拟Dom对象');
            return <></>;
        }
        // 当组件的个数大于等于2，或组件使用fragment标签包裹时
        // 则创建一个新的div用来挂在ref属性
        if (React.Children.count(props.children) >= 2 || props.children.type.toString() === 'Symbol(react.fragment)') {
            return <div ref={ref}>{stat ? props.children : ''}</div>;
        }
        // clone该组件并生成ref属性在children中,
        return React.cloneElement(!curIsDestroy.current || stat ? props.children : <div />, { ref });
        // TODO:根据多个children做多项不同的操作
        // return React.Children.map(props.children, child => {
        //  return React.cloneElement(child, {
        //    ref: dosomething
        //  })
        //   })
    },
    // (next, pre) => {
    //     if (next.children !== pre.children) {
    //         return true;
    //     }
    //     return false;
    // }
);

/**
  这段代码是导出一个自定义的 React useLazyRender，可以用于实现组件的懒加载。它接受一些可选的配置参数，包括：
  - forceVisible: 强制显示内容，即不进行懒加载，直接渲染组件的内容。
  - placeholder: 懒加载时显示的占位组件。
  - placeholderWrapperClassName: 占位组件的样式类名。
  - content: 组件的内容。
  - debugLog: 是否启用调试模式，用于记录懒加载次数。
  - Persistent: 是否启用持久化懒加载
  该 hook 内部使用了 ahooks 库中的 useInViewport 和 useDebounceFn 钩子来判断组件是否进入视口并进行防抖处理，以此实现懒加载。
  如果配置了 forceVisible，则直接渲染 content，否则渲染 placeholder。
  同时，该 Hook 也提供了一个可选的调试模式，用于记录组件被懒加载的次数。
*/

/**@example
 *  <LazyRender
 *  // 下面是选择性配置项，可根据实际情况设置
    placeholder={<div>Loading...</div>}
    placeholderWrapperClassName="my-placeholder"
    forceVisible={false}
    debugLog={true}
    children={ <div>Content</div>}
/>
 * 
*/

export default function LazyRender({
  forceVisible = false,
  placeholder: placeholderNode,
  placeholderWrapperClassName,
  children: content,
  debugLog = false,
  Persistent= false,
  ...lazyRenderConfig
}: UseLazyRenderOptions): ReactNode{
  const [visible, setVisible] = useState(!!lazyRenderConfig ? false : true)
  const ref = useRef(null)
  const [inViewport] = useInViewport(ref, {
    threshold: 0,
    ...(isObject(lazyRenderConfig) ? (lazyRenderConfig as any) : {}),
  })
  const show = useDebounceFn((props) => setVisible(props), {
    wait: 128,
    ...(isObject(lazyRenderConfig) ? (lazyRenderConfig as any) : {}),
  })

  useEffect(() => {
    if (Persistent) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          show.run(entry.isIntersecting)
        },
        {
          threshold: 0,
          ...(isObject(lazyRenderConfig) ? (lazyRenderConfig as any) : {})
        }
      );
      if (ref.current) {
        observer.observe(ref.current);
      }

      return () => observer.unobserve(ref.current);
    }
  }, []);
  

  useMemo(() => {
    if (!debugLog) {
      return
    }
    if (visible) {
      times++
      logRenderTimes()
    }
  }, [debugLog, visible])
  useMemo(() => {
    show.cancel()
    if (!inViewport) {
      return
    }
    show.run(true)
  }, [inViewport])

  useUnmount(show.run)

  const placeholder = (
    <div className={placeholderWrapperClassName} ref={ref}>
      {placeholderNode}
    </div>
  )

  return visible || forceVisible ? content : placeholder
}
